<template>
  <b-card no-body>
    <b-card-body>
      <postal-code-list-filter
        :values="filterValues"
        :filter-submitting="isLoading"
        @filter="setFilter"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'country_code'">
            {{ props.row.country_code || '-' }}
          </span>
          <span v-else-if="props.column.field === 'zipcode'">
            {{ props.row.zipcode || '-' }}
          </span>
          <span v-else-if="props.column.field === 'state'">
            {{ props.row.state || '-' }}
          </span>
          <span v-else-if="props.column.field === 'province'">
            {{ props.row.province || '-' }}
          </span>
          <span v-else-if="props.column.field === 'community'">
            {{ props.row.community || '-' }}
          </span>
          <span v-else-if="props.column.field === 'place'">
            {{ props.row.place || '-' }}
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="editPostalCode(props.row.id)"
              >
                <feather-icon icon="Edit2Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="$t('Edit Postal Code')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>

              <b-button
                :id="`delete-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deletePostalCode(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-button-${props.row.id}`"
                  :title="$t('Delete Postal Code')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import PostalCodeListFilter from '@/views/pages/postal_codes/PostalCodeListFilter.vue';
import useService from '@/auth/service/useService';
import store from '@/store/index';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    PostalCodeListFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        zipcode: '',
        country_code: ''
      },
      defaultFilterValues: {
        zipcode: '',
        country_code: ''
      }
    };
  },
  computed: {
    ...mapGetters({}),
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Country Code'),
          field: 'country_code'
        },
        {
          label: this.$t('State'),
          field: 'state'
        },
        {
          label: this.$t('Province'),
          field: 'province'
        },
        {
          label: this.$t('Community'),
          field: 'community'
        },
        {
          label: this.$t('Place'),
          field: 'place'
        },
        {
          label: this.$t('Zip Code'),
          field: 'zipcode'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return false;
      }

      this.isLoading = true;
      useService
        .getAllPostalCodes(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });

      return true;
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },
    editPostalCode(id) {
      this.$router.push(`/postal_codes/${id}`);
    },
    deletePostalCode(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Postal Code Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.deletePostalCode(id).then(response => {
            this.showSuccessMessage(
              `${response.message || this.$t('Delete Postal Code Success Message')}`
            );

            const index = this.rows.findIndex(item => item.id === id);
            this.rows.splice(index, 1);
          });
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
